// src/components/email/InlineEmailHtmlContent.tsx
import React, { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import { CircularProgress, Box, Typography } from "@mui/material";

interface InlineEmailHtmlContentProps {
  url: string;
}

// Helper function to truncate HTML for logging
const logHtmlContent = (html: string) => {
    if (!html) return;
    
    // Calculate size for logging
    const sizeInMB = new Blob([html]).size / (1024 * 1024);
    ////console.log(`HTML content size: ${sizeInMB.toFixed(2)}MB`);
    
    // Instead of trying to store the whole HTML, extract and store just the essential parts
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Get body content without images
      const tempDoc = parser.parseFromString(html, 'text/html');
      const images = tempDoc.querySelectorAll('img');
      images.forEach(img => {
        img.parentNode?.removeChild(img);
      });
      
      // Get text content and structure only
      const bodyTextOnly = tempDoc.body.innerHTML;
      const key = `email_text_${Date.now()}`;
      
      // Store the first 500KB only to avoid quota issues
      const maxStorageSize = 500000; // 500KB
      const textToStore = bodyTextOnly.length > maxStorageSize ? 
        bodyTextOnly.substring(0, maxStorageSize) + "... [truncated]" : 
        bodyTextOnly;
      
      localStorage.setItem(key, textToStore);
      ////console.log(`Essential HTML content (without images) saved to localStorage with key: ${key}`);
      
      // Extract and store email metadata separately
      const metadata = {
        timestamp: new Date().toISOString(),
        size: sizeInMB,
        hasImages: !!doc.querySelectorAll('img').length,
        imageCount: doc.querySelectorAll('img').length,
        hasTables: !!doc.querySelectorAll('table').length,
        tableCount: doc.querySelectorAll('table').length,
        elementCount: doc.getElementsByTagName('*').length,
        textLength: doc.body.textContent?.length || 0
      };
      
      localStorage.setItem(`${key}_metadata`, JSON.stringify(metadata));
      
      // Extract and store image sources separately if there aren't too many
      const allImages = doc.querySelectorAll('img');
      if (allImages.length > 0 && allImages.length <= 20) {
        const imageSources = Array.from(allImages)
          .map(img => img.src)
          .filter(src => src)
          .slice(0, 10); // Limit to 10 images
        
        localStorage.setItem(`${key}_images`, JSON.stringify(imageSources));
        ////console.log(`${imageSources.length} image URLs saved to localStorage`);
      }
    } catch (err) {
      console.warn("Could not extract and save HTML content:", err);
    }
  

    
    ////console.log("Fetched HTML content (truncated):", truncated);
    
    // Log HTML structure summary
    try {
     
  
      
      // Log basic document structure
      ////console.log("HTML Document Structure:", {
    //     title: doc.title || 'No title',
    //     bodyElementCount: doc.body.children.length,
    //     hasImages: !!doc.querySelectorAll('img').length,
    //     imageCount: doc.querySelectorAll('img').length,
    //     hasTables: !!doc.querySelectorAll('table').length,
    //     tableCount: doc.querySelectorAll('table').length,
    //     stylesheets: doc.querySelectorAll('link[rel="stylesheet"]').length,
    //     inlineStyles: doc.querySelectorAll('style').length,
    //     scripts: doc.querySelectorAll('script').length,
    //     totalElementCount: doc.getElementsByTagName('*').length
    //   });
      
    //   // Extract and log text content
    //   const textContent = doc.body.textContent || '';
    //   ////console.log("Text content summary:", {
    //     length: textContent.length,
    //     preview: textContent.substring(0, 150) + (textContent.length > 150 ? '...' : '')
    //   });
      
    
   
    } catch (err) {
      console.warn("Could not parse HTML for structure analysis:", err);
    }
  };

const InlineEmailHtmlContent: React.FC<InlineEmailHtmlContentProps> = ({ url }) => {
  const [htmlContent, setHtmlContent] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const fetchHtml = async () => {
      ////console.log("Fetching HTML from URL:", url);
      try {
        const response = await fetch(url, {
          signal,
          headers: {
            "Content-Type": "text/html",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const text = await response.text();
        
        // Log HTML content in a more manageable way
        logHtmlContent(text);
        
        if (!text) throw new Error("Empty response content");

        setHtmlContent(text);
        setError("");
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          console.error("Fetch error:", err);
          setError(err.message || "Failed to load email content. Check CORS configuration.");
        }
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };

    fetchHtml();

    return () => controller.abort();
  }, [url]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ ml: 2 }}>Loading email content...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, border: "1px solid error.main", borderRadius: 1 }}>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          Technical details: {url}
        </Typography>
      </Box>
    );
  }

  const sanitizedHtml = DOMPurify.sanitize(htmlContent, {
    ADD_TAGS: ["style"],
    ADD_ATTR: ["frameborder", "allowfullscreen"]
  });

  return (
    <Box sx={{ 
      overflow: "auto", 
      maxHeight: 300,
      "& iframe": {
        width: "100%",
        border: "none"
      }
    }}>
      <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
    </Box>
  );
};

export default InlineEmailHtmlContent;